import React, { useEffect, useState } from 'react';
import { useReseller } from '../../contexts/ResellerContext';
import './Footer.css';

function parseThemeColor(raw) {
  if (!raw?.trim()?.startsWith('#')) return null;
  const hex6 = raw.trim().match(/^#[0-9A-Fa-f]{6}$/)?.[0];
  const hex3 = raw.trim().match(/^#[0-9A-Fa-f]{3}$/)?.[0];
  if (hex6) return hex6;
  if (hex3) return hex3.replace(/([0-9A-Fa-f])/g, '$1$1');
  return null;
}

function toAbsoluteUrl(url, baseUrl) {
  if (!url?.trim()) return url;
  let u = url.trim();
  // Fix malformed protocol: https//: or http//: -> https:// or http://
  u = u.replace(/^https\/\/:/, 'https://').replace(/^http\/\/:/, 'http://');
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  const base = baseUrl.replace(/\/$/, '');
  return u.startsWith('/') ? `${base}${u}` : `${base}/${u}`;
}

function Footer() {
  const { reseller } = useReseller();
  const [showFooter, setShowFooter] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      // Show footer when scrolled near the bottom (within 20px)
      const isBottom = scrollTop + windowHeight >= documentHeight - 20;
      
      // Add a small delay to avoid flickering
      if (isBottom) {
        setTimeout(() => setShowFooter(true), 100);
      } else {
        setTimeout(() => setShowFooter(false), 100);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    // Initial check after a short delay to ensure accurate measurements
    setTimeout(() => handleScroll(), 300);
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const themeColor = reseller ? parseThemeColor(reseller.themeColor) : null;
  const footerStyle = themeColor ? { backgroundColor: themeColor } : undefined;

  return (
    <footer className={`footer ${showFooter ? 'visible' : ''}`} style={footerStyle}>
      <div className="footer-content">
        <div className="footer-text">
          {reseller?.copyrightText ? (
            <p>
              {reseller.copyrightText.split('\n').map((line, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <br />}
                  {line}
                </React.Fragment>
              ))}
            </p>
          ) : (
            <>
              <p>© {new Date().getFullYear()} Chattitude. All rights reserved.</p>
              <p>Chattitude - Conversational AI agents that sell, support, and scale with you.</p>
            </>
          )}
        </div>
        <div className="social-icons">
          {[
            { url: reseller?.youtubeUrl, defaultUrl: 'https://www.youtube.com/channel/UC-GJyi2t4ZZnl57jVbTcb5Q', baseUrl: 'https://www.youtube.com', icon: 'fab fa-youtube', label: 'YouTube' },
            { url: reseller?.xUrl, defaultUrl: 'https://x.com/chattitudeai', baseUrl: 'https://x.com', icon: 'fa-brands fa-x-twitter', label: 'X (Twitter)' },
            { url: reseller?.facebookUrl, defaultUrl: 'https://facebook.com/chattitude', baseUrl: 'https://facebook.com', icon: 'fab fa-facebook-f', label: 'Facebook' },
            { url: reseller?.linkedinUrl, defaultUrl: 'https://www.linkedin.com/company/chattitude', baseUrl: 'https://www.linkedin.com', icon: 'fab fa-linkedin-in', label: 'LinkedIn' },
            { url: reseller?.tiktokUrl, defaultUrl: 'https://www.tiktok.com/@chattitudeai', baseUrl: 'https://www.tiktok.com', icon: 'fab fa-tiktok', label: 'TikTok' },
          ]
            .filter(({ url, defaultUrl }) => (reseller?.id != null ? url?.trim() : defaultUrl))
            .map(({ url, defaultUrl, baseUrl, icon, label }) => {
              const href = reseller?.id != null ? toAbsoluteUrl(url, baseUrl) : defaultUrl;
              return (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}>
                  <i className={icon}></i>
                </a>
              );
            })}
        </div>
      </div>
    </footer>
  );
}

export default Footer;